import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Button, Group, TextInput, Select, Title, Pagination, Badge, ActionIcon, Card, Text, Image } from '@mantine/core'
import { IconPlus, IconSearch, IconEdit, IconTrash, IconList } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api } from '@/lib/api'

export default function CourseList() {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Record<string, unknown>[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string | null>('Бүгд')
  const [categories, setCategories] = useState<string[]>(['Бүгд'])
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    if (category && category !== 'Бүгд') params.category = category
    if (search) params.search = search
    api.admin.getCourses(params).then((res) => {
      setCourses(res.data)
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
    })
  }, [pagination.page, category, search])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    api.admin.getCategories().then((res) => {
      setCategories(['Бүгд', ...res.courseCategories])
    }).catch(() => {})
  }, [])

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteCourse(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Сургалт устгагдлаа', color: 'green' })
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Устгахад алдаа гарлаа', color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={2}>Сургалтууд</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/courses/new')}>
          Шинэ сургалт
        </Button>
      </Group>

      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Select
          data={categories}
          value={category}
          onChange={(v) => { setCategory(v); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ minWidth: 150 }}
        />
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Гарчиг</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Ангилал</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үнэ</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Суралцагч</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Статус</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {courses.map((c, i) => (
                <Table.Tr key={c._id as string}>
                  <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Image src={c.thumbnailUrl as string} w={56} h={36} radius="sm" fit="cover" fallbackSrc="https://placehold.co/56x36?text=..." />
                      <Text size="sm" fw={500} lineClamp={1}>{c.title as string}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>{c.category as string}</Table.Td>
                  <Table.Td fw={500}>
                    {c.priceType === 'free' || !c.priceType
                      ? <Badge variant="light" color="green" size="sm">Үнэгүй</Badge>
                      : c.priceType === 'premium'
                        ? <Badge variant="light" color="violet" size="sm">
                          Премиум {String(c.premiumDuration)}с
                        </Badge>
                        : `${(c.price as number)?.toLocaleString()}₮`}
                  </Table.Td>
                  <Table.Td fw={500}>{c.studentCount as number}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={c.status === 'published' ? 'green' : 'gray'}>
                      {c.status === 'published' ? 'Нийтлэгдсэн' : 'Ноорог'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <ActionIcon variant="light" color="blue" radius="md" onClick={() => navigate(`/courses/${c._id}/edit`)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="cyan" radius="md" onClick={() => navigate(`/courses/${c._id}/lessons`)}>
                        <IconList size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(c._id as string)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {courses.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text ta="center" c="dimmed" py="xl">Сургалт олдсонгүй</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Card>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </div>
  )
}
